import { XAxis, YAxis, CartesianGrid, Legend, LineChart, Line } from 'recharts';
import { format } from 'date-fns';
import { sortBy } from '../../utils/sortBy';
import {
  type BadgeAwardsChartParams,
  useBadgesAwardsChartData,
} from '../../api/hooks/useBadgesAwardsChartData';
import { CenteredSpinner } from '../../components/custom/CenteredSpinner';
import { useListFilterContext, useTranslate } from 'ra-core';
import { fillWithEmptyDataForChart } from '../../utils/fillWithEmptyDataForChart';
import { useId, useMemo, useState } from 'react';
import { Switch } from '../../components/ui/switch';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../components/ui/chart';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

type BadgeAwardsChartProps = {
  title: string;
  name: string;
} & Pick<BadgeAwardsChartParams, 'badgeIdFilter'>;

type BadgeAwardsFilterValues = {
  createdAfter?: string;
  createdBefore?: string;
  badgesId?: string[];
};

export const BadgesAwardsChart = ({
  badgeIdFilter,
  ...props
}: BadgeAwardsChartProps) => {
  const { filterValues: untypedFilterValues } = useListFilterContext();
  const [shouldFillEmptyData, setShouldFillEmptyData] = useState(false);
  const translate = useTranslate();
  const switchId = useId();

  // Type assertion to add types to filterValues
  const filterValues: BadgeAwardsFilterValues = untypedFilterValues;

  const { data: chartData, isPending } = useBadgesAwardsChartData({
    createdAfter: filterValues.createdAfter,
    createdBefore: filterValues.createdBefore,
    badgeIdFilter: filterValues.badgesId ?? badgeIdFilter,
  });

  const sortedData = useMemo(() => {
    if (!chartData) {
      return [];
    }

    const rawData = shouldFillEmptyData
      ? fillWithEmptyDataForChart(chartData)
      : chartData;

    // Ensure count is always a number and sort by date
    return sortBy(
      rawData.map((entry) => ({
        ...entry,
        count: Number(entry.count as string | number),
      })),
      (x) => x.date
    );
  }, [chartData, shouldFillEmptyData]);

  const chartConfig = {
    count: { label: props.name, color: 'var(--chart-1)' },
  } satisfies ChartConfig;

  if (isPending && !filterValues.createdAfter && !filterValues.createdBefore) {
    return <CenteredSpinner />;
  }

  return (
    <Card className="shadow-md">
      <CardHeader className="flex flex-wrap items-center justify-between gap-4">
        <CardTitle>{props.title}</CardTitle>
        <div className="flex items-center gap-2 text-sm">
          <Switch
            id={switchId}
            checked={shouldFillEmptyData}
            onCheckedChange={setShouldFillEmptyData}
          />
          <label htmlFor={switchId}>
            {translate('moonstar.badges.analytics.showDaysWithoutAwards')}
          </label>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="h-[400px] w-full aspect-auto"
        >
          <LineChart data={sortedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) =>
                format(new Date(date as string | Date), 'MMM dd')
              }
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              stroke="var(--color-count)"
              tickFormatter={(value) => Math.round(value as number).toString()}
              allowDecimals={false}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="count"
              name={props.name}
              stroke="var(--color-count)"
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
